import { ChakraProvider } from '@chakra-ui/react';
import { useInjectedCssVarOfViewport } from '@cronos-app/chakra';
import {
  CookieConsentScripts,
  GoogleScripts,
  NoSSR,
} from '@cronos-app/components';
import { useOverrideGlobalContextStore } from '@cronos-app/wallet';
import { QueryClientProvider } from '@tanstack/react-query';
import { BugsnagErrorBoundary } from '@ui/components/bugsnag';
import ConditionalChakraVarOverrides from '@ui/components/ConditionalChakraVarOverrides';
import { useInitializeApp } from '@ui/hooks/useInitializeApp';
import { IntlProviderWithPageProps } from '@ui/locales/IntlProviderWithPageProps';
import { theme } from '@ui/theme';
import { Fonts } from '@ui/theme/Fonts';
import { dynamicWithLoading } from '@ui/utils/dynamicWithLoading';
import { queryClient } from '@ui/utils/queryClient';
import type { NextComponentType } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';

const DevUtils = dynamicWithLoading(() =>
  import('@cronos-app/components').then((mod) => mod.DevUtils),
);

interface NextAppProps extends AppProps {
  Component: NextComponentType;
}

function MyApp({ Component, pageProps }: NextAppProps) {
  useOverrideGlobalContextStore.setState({
    network: pageProps.defaultNetwork,
  });

  useInitializeApp();
  /**
   * Fix missing modal footer on iOS.
   * @see https://github.com/chakra-ui/chakra-ui/issues/4903
   */
  useInjectedCssVarOfViewport();

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <CookieConsentScripts />
      <GoogleScripts />
      <Fonts />
      <QueryClientProvider client={queryClient}>
        <IntlProviderWithPageProps pageProps={pageProps}>
          <ChakraProvider resetCSS theme={theme}>
            <BugsnagErrorBoundary>
              <Component {...pageProps} />
              {!process.env.IS_PROD && (
                <NoSSR>
                  <DevUtils />
                </NoSSR>
              )}
            </BugsnagErrorBoundary>
          </ChakraProvider>
        </IntlProviderWithPageProps>
      </QueryClientProvider>
      <ConditionalChakraVarOverrides />
    </>
  );
}

export default MyApp;
